import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import {
  Wallet, Send, Bell, ScrollText, Trash2, Filter, Copy, Loader2,
  Plus, Minus, CreditCard, CheckCircle2, RefreshCw, Gift,
} from 'lucide-react';
import { toast } from 'sonner';

// رصيد التعلّم: وحدة الرصيد التربوية
const UNIT = 'د.أ';

const TXN_LABELS = {
  request: 'طلب شحن',
  recharge: 'شحن ببطاقة',
  gift: '🎁 هدية من المعلم',
  adjust: 'إضافة من الدعم الفني',
  deduct: 'خصم من الدعم الفني',
  purchase: 'شراء',
};
const TXN_STYLES = {
  request: 'bg-amber-100 text-amber-800',
  recharge: 'bg-green-100 text-green-700',
  gift: 'bg-pink-100 text-pink-700',
  adjust: 'bg-blue-100 text-blue-700',
  deduct: 'bg-red-100 text-red-700',
  purchase: 'bg-red-100 text-red-700',
};

const fmtDate = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleString('ar-EG', { dateStyle: 'short', timeStyle: 'short' }); }
  catch { return iso; }
};

const TeacherWallet = ({ apiUrl, token }) => {
  const [tab, setTab] = useState('students'); // students | requests | log
  const [students, setStudents] = useState([]);
  const [requests, setRequests] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);

  // card dialog
  const [cardDialog, setCardDialog] = useState(null); // { student_id, student_name }
  const [cardAmount, setCardAmount] = useState('');
  const [cardNote, setCardNote] = useState('');
  const [creatingCard, setCreatingCard] = useState(false);
  const [issuedCard, setIssuedCard] = useState(null); // { code, amount }

  // adjust dialog
  const [adjustDialog, setAdjustDialog] = useState(null); // { student_id, student_name }
  const [adjustSign, setAdjustSign] = useState(1);
  const [adjustAmount, setAdjustAmount] = useState('');
  const [adjustNote, setAdjustNote] = useState('');
  const [adjusting, setAdjusting] = useState(false);

  // gift (هدية) dialog — one / several / all students
  const [giftOpen, setGiftOpen] = useState(false);
  const [giftAll, setGiftAll] = useState(false);
  const [giftSelected, setGiftSelected] = useState({}); // { student_id: true }
  const [giftAmount, setGiftAmount] = useState('');
  const [giftMsg, setGiftMsg] = useState('');
  const [gifting, setGifting] = useState(false);

  // log filters
  const [fStudent, setFStudent] = useState('all');
  const [fType, setFType] = useState('all');
  const [fFrom, setFFrom] = useState('');
  const [fTo, setFTo] = useState('');

  const authHeaders = useMemo(
    () => ({ 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }),
    [token],
  );

  const fetchStudents = useCallback(async () => {
    try {
      const r = await fetch(`${apiUrl}/api/teacher/balance/students`, { headers: authHeaders });
      if (r.ok) setStudents(await r.json());
    } catch { /* ignore */ }
  }, [apiUrl, authHeaders]);

  const fetchRequests = useCallback(async () => {
    try {
      const r = await fetch(`${apiUrl}/api/teacher/balance/requests`, { headers: authHeaders });
      if (r.ok) setRequests(await r.json());
    } catch { /* ignore */ }
  }, [apiUrl, authHeaders]);

  const fetchTransactions = useCallback(async () => {
    const params = new URLSearchParams();
    if (fStudent !== 'all') params.set('student_id', fStudent);
    if (fType !== 'all') params.set('type', fType);
    if (fFrom) params.set('date_from', fFrom);
    if (fTo) params.set('date_to', fTo);
    try {
      const r = await fetch(`${apiUrl}/api/teacher/balance/transactions?${params.toString()}`, { headers: authHeaders });
      if (r.ok) setTransactions(await r.json());
    } catch { /* ignore */ }
  }, [apiUrl, authHeaders, fStudent, fType, fFrom, fTo]);

  const refreshAll = useCallback(async () => {
    setLoading(true);
    await Promise.all([fetchStudents(), fetchRequests(), fetchTransactions()]);
    setLoading(false);
  }, [fetchStudents, fetchRequests, fetchTransactions]);

  useEffect(() => { refreshAll(); /* eslint-disable-next-line */ }, [fetchStudents, fetchRequests]);
  useEffect(() => { fetchTransactions(); }, [fetchTransactions]);

  const openCardDialog = (student_id, student_name, prefillAmount = '') => {
    setCardDialog({ student_id, student_name });
    setCardAmount(prefillAmount ? String(prefillAmount) : '');
    setCardNote('');
    setIssuedCard(null);
  };

  const createCard = async () => {
    const amount = Number(cardAmount);
    if (!amount || amount <= 0) { toast.error('أدخل قيمة صحيحة'); return; }
    setCreatingCard(true);
    try {
      const r = await fetch(`${apiUrl}/api/teacher/balance/cards`, {
        method: 'POST', headers: authHeaders,
        body: JSON.stringify({ student_id: cardDialog.student_id, amount, note: cardNote }),
      });
      const data = await r.json();
      if (r.ok) {
        setIssuedCard(data.card);
        toast.success('تم إنشاء بطاقة الشحن');
        fetchRequests(); fetchTransactions();
      } else {
        toast.error(data.detail || 'فشل إنشاء البطاقة');
      }
    } catch { toast.error('فشل الاتصال بالخادم'); }
    setCreatingCard(false);
  };

  const submitAdjust = async () => {
    if (!adjustDialog) return;
    const val = Math.abs(Number(adjustAmount));
    if (!val) { toast.error('قيمة غير صالحة'); return; }
    setAdjusting(true);
    try {
      const r = await fetch(`${apiUrl}/api/teacher/balance/adjust`, {
        method: 'POST', headers: authHeaders,
        body: JSON.stringify({
          student_id: adjustDialog.student_id,
          amount: adjustSign * val,
          note: adjustNote || (adjustSign > 0 ? 'إضافة يدوية' : 'خصم يدوي'),
        }),
      });
      const data = await r.json();
      if (r.ok) {
        toast.success('تم تعديل الرصيد');
        setAdjustDialog(null);
        fetchStudents(); fetchTransactions();
      } else toast.error(data.detail || 'فشل التعديل');
    } catch { toast.error('فشل الاتصال بالخادم'); }
    setAdjusting(false);
  };

  const openAdjust = (student_id, student_name, sign) => {
    setAdjustDialog({ student_id, student_name });
    setAdjustSign(sign);
    setAdjustAmount('');
    setAdjustNote('');
  };

  const openGift = (student_id = null) => {
    setGiftAll(false);
    setGiftSelected(student_id ? { [student_id]: true } : {});
    setGiftAmount('');
    setGiftMsg('');
    setGiftOpen(true);
  };

  const toggleGiftStudent = (id) => setGiftSelected((m) => ({ ...m, [id]: !m[id] }));
  const giftCount = giftAll ? students.length : Object.values(giftSelected).filter(Boolean).length;

  const submitGift = async () => {
    const val = Number(giftAmount);
    if (!val || val <= 0) { toast.error('أدخل قيمة هدية صحيحة'); return; }
    const ids = Object.keys(giftSelected).filter((id) => giftSelected[id]);
    if (!giftAll && ids.length === 0) { toast.error('اختر طالباً واحداً على الأقل أو «كل الطلاب»'); return; }
    setGifting(true);
    try {
      const r = await fetch(`${apiUrl}/api/teacher/balance/gift`, {
        method: 'POST', headers: authHeaders,
        body: JSON.stringify({
          all_students: giftAll,
          student_ids: giftAll ? [] : ids,
          amount: val,
          note: giftMsg,
        }),
      });
      const data = await r.json();
      if (r.ok) {
        toast.success(data.message || 'تم إرسال الهدية');
        setGiftOpen(false);
        fetchStudents(); fetchTransactions();
      } else toast.error(data.detail || 'فشل إرسال الهدية');
    } catch { toast.error('فشل الاتصال بالخادم'); }
    setGifting(false);
  };

  const deleteTxn = async (txn) => {
    if (!window.confirm('حذف هذا السجل؟')) return;
    try {
      const r = await fetch(`${apiUrl}/api/teacher/balance/transactions/${txn.id}`, {
        method: 'DELETE', headers: authHeaders,
      });
      if (r.ok) { toast.success('تم حذف السجل'); fetchTransactions(); }
      else toast.error('فشل الحذف');
    } catch { toast.error('فشل الاتصال بالخادم'); }
  };

  const clearLog = async () => {
    if (!window.confirm('مسح كل سجل الأرصدة (حسب الفلتر الحالي للطالب)؟')) return;
    const params = new URLSearchParams();
    if (fStudent !== 'all') params.set('student_id', fStudent);
    try {
      const r = await fetch(`${apiUrl}/api/teacher/balance/transactions?${params.toString()}`, {
        method: 'DELETE', headers: authHeaders,
      });
      if (r.ok) { toast.success('تم مسح السجل'); fetchTransactions(); }
      else toast.error('فشل المسح');
    } catch { toast.error('فشل الاتصال بالخادم'); }
  };

  const copyCode = (code) => {
    try { navigator.clipboard.writeText(code); toast.success('تم نسخ الكود'); } catch { /* ignore */ }
  };

  const totalBalance = students.reduce((s, x) => s + (x.balance || 0), 0);

  // Charge-report totals for the currently-filtered log: how much credit was CHARGED
  // (cards + gifts + manual top-ups) vs CONSUMED (folder purchases + manual deducts).
  // Pending requests carry no real balance change, so they're excluded.
  const summary = useMemo(() => {
    let charged = 0, consumed = 0, chargedCount = 0, consumedCount = 0;
    let cards = 0, gifts = 0, manualIn = 0, requestsPending = 0;
    for (const t of transactions) {
      const amt = Number(t.amount) || 0;
      if (t.type === 'request') {
        if (t.status === 'pending') requestsPending += 1;
        continue; // a request is not a balance movement
      }
      if (t.type === 'recharge') { charged += amt; chargedCount += 1; cards += amt; }
      else if (t.type === 'gift') { charged += amt; chargedCount += 1; gifts += amt; }
      else if (t.type === 'adjust') {
        if (amt >= 0) { charged += amt; chargedCount += 1; manualIn += amt; }
        else { consumed += Math.abs(amt); consumedCount += 1; }
      } else if (t.type === 'deduct') { consumed += Math.abs(amt); consumedCount += 1; }
    }
    const round2 = (n) => Math.round(n * 100) / 100;
    return {
      charged: round2(charged), consumed: round2(consumed), net: round2(charged - consumed),
      chargedCount, consumedCount, cards: round2(cards), gifts: round2(gifts),
      manualIn: round2(manualIn), requestsPending,
    };
  }, [transactions]);

  return (
    <div className="space-y-3 bg-gradient-to-br from-[#FAF6EC] to-[#F1E8D2] rounded-2xl p-3 md:p-4 border border-[#E8DFCF]" dir="rtl">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-2 px-1">
        <div className="flex items-center gap-2 text-[#412402]">
          <Wallet className="w-5 h-5 text-amber-700" />
          <span className="font-bold">أرصدة التعلّم</span>
          <span className="text-xs text-[#412402]/50">— إجمالي {totalBalance} {UNIT}</span>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" className="text-pink-600 border-pink-200 hover:bg-pink-50" onClick={() => openGift()} data-testid="bulk-gift-btn">
            <Gift className="w-4 h-4 ml-1" /> إهداء رصيد
          </Button>
          <Button variant="outline" size="sm" onClick={refreshAll}>
            <RefreshCw className="w-4 h-4 ml-1" /> تحديث
          </Button>
        </div>
      </div>

      {/* Inner tabs */}
      <div className="flex items-center gap-1 bg-white/70 border border-[#E5E5E5] rounded-xl p-1 text-sm w-fit">
        {[
          { id: 'students', label: 'أرصدة الطلاب', icon: Wallet },
          { id: 'requests', label: `طلبات الشحن${requests.length ? ` (${requests.length})` : ''}`, icon: Bell },
          { id: 'log', label: 'سجل الأرصدة', icon: ScrollText },
        ].map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`flex items-center gap-1 px-3 py-1.5 rounded-lg font-medium transition-colors ${
              tab === t.id ? 'bg-amber-700 text-white' : 'text-[#412402] hover:bg-amber-50'
            }`}
            data-testid={`wallet-tab-${t.id}`}
          >
            <t.icon className="w-4 h-4" /> {t.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="py-16 text-center"><Loader2 className="w-6 h-6 animate-spin text-amber-600 mx-auto" /></div>
      ) : tab === 'students' ? (
        <div className="bg-white/80 border border-[#E5E5E5] rounded-xl p-3 space-y-2">
          {students.length === 0 ? (
            <div className="py-12 text-center text-[#412402]/50 text-sm">لا يوجد طلاب مفعّلون.</div>
          ) : students.map((s) => (
            <div key={s.student_id} className="flex items-center gap-3 p-2 rounded-lg border border-[#E8DFCF] bg-white">
              <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center text-amber-700 font-bold shrink-0">
                {(s.student_name || '?').charAt(0)}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-[#412402] text-sm truncate">{s.student_name}</p>
                <p className="text-[11px] text-[#412402]/50">{s.grade}{s.section ? ` / ${s.section}` : ''}</p>
              </div>
              <Badge className="bg-amber-50 text-amber-800 border border-amber-200 shrink-0">
                {s.balance || 0} {UNIT}
              </Badge>
              <div className="flex items-center gap-1 shrink-0">
                <Button size="sm" variant="ghost" className="text-green-700" title="إضافة" onClick={() => openAdjust(s.student_id, s.student_name, 1)}>
                  <Plus className="w-4 h-4" />
                </Button>
                <Button size="sm" variant="ghost" className="text-red-600" title="خصم" onClick={() => openAdjust(s.student_id, s.student_name, -1)}>
                  <Minus className="w-4 h-4" />
                </Button>
                <Button size="sm" variant="ghost" className="text-pink-600" title="إهداء رصيد" onClick={() => openGift(s.student_id)} data-testid={`gift-btn-${s.student_id}`}>
                  <Gift className="w-4 h-4" />
                </Button>
                <Button size="sm" className="bg-amber-700 hover:bg-amber-800" onClick={() => openCardDialog(s.student_id, s.student_name)}>
                  <CreditCard className="w-4 h-4 ml-1" /> بطاقة شحن
                </Button>
              </div>
            </div>
          ))}
        </div>
      ) : tab === 'requests' ? (
        <div className="bg-white/80 border border-[#E5E5E5] rounded-xl p-3 space-y-2">
          {requests.length === 0 ? (
            <div className="py-12 text-center text-[#412402]/50 text-sm">لا توجد طلبات شحن معلّقة.</div>
          ) : requests.map((rq) => (
            <div key={rq.id} className="flex items-center gap-3 p-2 rounded-lg border border-amber-200 bg-amber-50/50">
              <Bell className="w-5 h-5 text-amber-700 shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="font-medium text-[#412402] text-sm truncate">{rq.student_name}</p>
                <p className="text-[11px] text-[#412402]/60">
                  يطلب {rq.amount} {UNIT} • {fmtDate(rq.created_at)}{rq.note ? ` • ${rq.note}` : ''}
                </p>
                {rq.reference_number ? (
                  <p className="text-[11px] font-semibold text-amber-800 mt-0.5">
                    الرقم المرجعي: <span className="font-mono">{rq.reference_number}</span>
                  </p>
                ) : null}
              </div>
              <Button size="sm" className="bg-amber-700 hover:bg-amber-800 shrink-0"
                onClick={() => openCardDialog(rq.student_id, rq.student_name, rq.amount)}>
                <Send className="w-4 h-4 ml-1" /> إرسال بطاقة
              </Button>
            </div>
          ))}
        </div>
      ) : (
        <div className="space-y-3">
          {/* Filters */}
          <div className="grid sm:grid-cols-4 gap-2 p-3 bg-white border border-[#E5E5E5] rounded-xl">
            <div className="space-y-1">
              <Label className="text-xs text-[#412402]/70 flex items-center gap-1"><Filter className="w-3 h-3" /> الطالب</Label>
              <Select value={fStudent} onValueChange={setFStudent}>
                <SelectTrigger data-testid="log-filter-student"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">جميع الطلاب</SelectItem>
                  {students.map((s) => <SelectItem key={s.student_id} value={s.student_id}>{s.student_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-[#412402]/70">نوع الحركة</Label>
              <Select value={fType} onValueChange={setFType}>
                <SelectTrigger data-testid="log-filter-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">كل الأنواع</SelectItem>
                  <SelectItem value="request">طلب شحن</SelectItem>
                  <SelectItem value="recharge">شحن ببطاقة</SelectItem>
                  <SelectItem value="gift">🎁 هدية من المعلم</SelectItem>
                  <SelectItem value="adjust">إضافة من الدعم الفني</SelectItem>
                  <SelectItem value="deduct">خصم من الدعم الفني</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-[#412402]/70">من تاريخ</Label>
              <Input type="date" value={fFrom} onChange={(e) => setFFrom(e.target.value)} />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-[#412402]/70">إلى تاريخ</Label>
              <Input type="date" value={fTo} onChange={(e) => setFTo(e.target.value)} />
            </div>
            <div className="sm:col-span-4 flex justify-between">
              <Button variant="ghost" size="sm" onClick={() => { setFStudent('all'); setFType('all'); setFFrom(''); setFTo(''); }}>
                إعادة ضبط الفلاتر
              </Button>
              <Button variant="outline" size="sm" className="text-red-600" onClick={clearLog}>
                <Trash2 className="w-4 h-4 ml-1" /> مسح السجل
              </Button>
            </div>
          </div>

          {/* Charge-report summary (respects the filters above) */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <div className="rounded-xl border border-green-200 bg-green-50 p-3" data-testid="report-total-charged">
              <p className="text-[11px] text-green-700/80">إجمالي المشحون</p>
              <p className="text-lg font-bold text-green-700">{summary.charged} {UNIT}</p>
              <p className="text-[10px] text-green-700/60">{summary.chargedCount} حركة</p>
            </div>
            <div className="rounded-xl border border-red-200 bg-red-50 p-3" data-testid="report-total-consumed">
              <p className="text-[11px] text-red-700/80">إجمالي المستهلك</p>
              <p className="text-lg font-bold text-red-700">{summary.consumed} {UNIT}</p>
              <p className="text-[10px] text-red-700/60">{summary.consumedCount} حركة</p>
            </div>
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-3" data-testid="report-net">
              <p className="text-[11px] text-amber-800/80">الصافي (المتبقّي)</p>
              <p className="text-lg font-bold text-amber-800">{summary.net} {UNIT}</p>
              <p className="text-[10px] text-amber-800/60">مشحون − مستهلك</p>
            </div>
            <div className="rounded-xl border border-[#E8DFCF] bg-white p-3" data-testid="report-breakdown">
              <p className="text-[11px] text-[#412402]/60">تفصيل المشحون</p>
              <p className="text-[11px] text-[#412402]/80">💳 بطاقات: <b>{summary.cards}</b></p>
              <p className="text-[11px] text-[#412402]/80">🎁 هدايا: <b>{summary.gifts}</b> · ✋ يدوي: <b>{summary.manualIn}</b></p>
              {summary.requestsPending > 0 && (
                <p className="text-[10px] text-amber-700 mt-0.5">{summary.requestsPending} طلب بانتظار البطاقة</p>
              )}
            </div>
          </div>

          {/* Log table */}
          <div className="bg-white/80 border border-[#E5E5E5] rounded-xl p-3">
            {transactions.length === 0 ? (
              <div className="py-12 text-center text-[#412402]/50 text-sm">لا توجد حركات مطابقة.</div>
            ) : (
              <div className="space-y-1">
                {transactions.map((t) => (
                  <div key={t.id} className="flex items-center gap-2 p-2 rounded-lg border border-[#E8DFCF] bg-white text-sm">
                    <Badge className={`${TXN_STYLES[t.type] || 'bg-gray-100 text-gray-600'} text-[10px] shrink-0`}>
                      {TXN_LABELS[t.type] || t.type}
                    </Badge>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-[#412402] truncate">{t.student_name}</p>
                      <p className="text-[11px] text-[#412402]/50">
                        {fmtDate(t.created_at)}
                        {t.reference_number ? ` • مرجعي: ${t.reference_number}` : ''}
                        {t.code ? ` • كود ${t.code}` : ''}
                        {t.note ? ` • ${t.note}` : ''}
                        {t.status === 'pending' ? ' • (بانتظار البطاقة)' : ''}
                      </p>
                    </div>
                    <span className="font-bold shrink-0 text-[#412402]">{t.amount} {UNIT}</span>
                    {t.status !== 'pending' && t.balance_after != null && (
                      <span className="text-[11px] text-[#412402]/50 shrink-0">الرصيد: {t.balance_after}</span>
                    )}
                    <Button size="sm" variant="ghost" className="text-red-600 shrink-0" onClick={() => deleteTxn(t)}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Create-card dialog */}
      <Dialog open={!!cardDialog} onOpenChange={(v) => { if (!v) setCardDialog(null); }}>
        <DialogContent dir="rtl" className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-amber-700" /> بطاقة شحن لـ {cardDialog?.student_name}
            </DialogTitle>
            <DialogDescription>أنشئ بطاقة بقيمة محددة، ثم سلّم الكود للطالب ليشحن رصيده.</DialogDescription>
          </DialogHeader>

          {issuedCard ? (
            <div className="space-y-3 text-center py-2">
              <CheckCircle2 className="w-12 h-12 text-green-600 mx-auto" />
              <p className="text-sm text-[#412402]/70">تم إنشاء بطاقة بقيمة <b>{issuedCard.amount} {UNIT}</b></p>
              <div className="flex items-center justify-center gap-2 bg-amber-50 border border-amber-200 rounded-lg p-3">
                <span className="font-mono text-lg font-bold tracking-widest text-amber-800">{issuedCard.code}</span>
                <Button size="sm" variant="ghost" onClick={() => copyCode(issuedCard.code)}><Copy className="w-4 h-4" /></Button>
              </div>
              <p className="text-[11px] text-[#412402]/50">تم إرسال إشعار للطالب بالكود تلقائياً.</p>
            </div>
          ) : (
            <div className="space-y-3">
              <div>
                <Label>القيمة ({UNIT})</Label>
                <Input type="number" min={1} step="1" value={cardAmount}
                  onChange={(e) => setCardAmount(e.target.value)} placeholder="مثال: 10" data-testid="card-amount-input" />
              </div>
              <div>
                <Label>ملاحظة (اختياري)</Label>
                <Textarea rows={2} value={cardNote} onChange={(e) => setCardNote(e.target.value)} />
              </div>
            </div>
          )}

          <DialogFooter>
            {issuedCard ? (
              <Button onClick={() => setCardDialog(null)} className="bg-amber-700 hover:bg-amber-800">إغلاق</Button>
            ) : (
              <>
                <Button variant="outline" onClick={() => setCardDialog(null)}>إلغاء</Button>
                <Button onClick={createCard} disabled={creatingCard} className="bg-amber-700 hover:bg-amber-800" data-testid="create-card-btn">
                  {creatingCard ? <Loader2 className="w-4 h-4 animate-spin ml-2" /> : <Send className="w-4 h-4 ml-2" />}
                  إنشاء وإرسال
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Adjust balance dialog (popup instead of prompt) */}
      <Dialog open={!!adjustDialog} onOpenChange={(v) => { if (!v) setAdjustDialog(null); }}>
        <DialogContent dir="rtl" className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {adjustSign > 0 ? <Plus className="w-5 h-5 text-green-700" /> : <Minus className="w-5 h-5 text-red-600" />}
              {adjustSign > 0 ? 'إضافة رصيد' : 'خصم رصيد'} — {adjustDialog?.student_name}
            </DialogTitle>
            <DialogDescription>عدّل رصيد الطالب مباشرةً (يظهر في سجل الأرصدة).</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1 w-fit">
              <button
                onClick={() => setAdjustSign(1)}
                className={`px-3 py-1 rounded-md text-sm font-medium ${adjustSign > 0 ? 'bg-green-600 text-white' : 'text-gray-600'}`}
              >إضافة +</button>
              <button
                onClick={() => setAdjustSign(-1)}
                className={`px-3 py-1 rounded-md text-sm font-medium ${adjustSign < 0 ? 'bg-red-600 text-white' : 'text-gray-600'}`}
              >خصم −</button>
            </div>
            <div>
              <Label>القيمة ({UNIT})</Label>
              <Input type="number" min={1} step="1" value={adjustAmount}
                onChange={(e) => setAdjustAmount(e.target.value)} placeholder="مثال: 5" data-testid="adjust-amount-input" autoFocus />
            </div>
            <div>
              <Label>ملاحظة (اختياري)</Label>
              <Textarea rows={2} value={adjustNote} onChange={(e) => setAdjustNote(e.target.value)} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAdjustDialog(null)}>إلغاء</Button>
            <Button onClick={submitAdjust} disabled={adjusting} className={adjustSign > 0 ? 'bg-green-700 hover:bg-green-800' : 'bg-red-600 hover:bg-red-700'} data-testid="confirm-adjust-btn">
              {adjusting ? <Loader2 className="w-4 h-4 animate-spin ml-2" /> : null}
              تأكيد
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Gift (هدية) dialog — one / several / all students; announced in the chat */}
      <Dialog open={giftOpen} onOpenChange={setGiftOpen}>
        <DialogContent dir="rtl" className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Gift className="w-5 h-5 text-pink-600" /> إهداء رصيد
            </DialogTitle>
            <DialogDescription>
              تُضاف الهدية مباشرةً لرصيد كل طالب محدَّد، ويصله إشعار ورسالة في المحادثة تُظهر أنها هدية من المعلم.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {/* Recipients: selected students vs all */}
            <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1 w-fit">
              <button onClick={() => setGiftAll(false)}
                className={`px-3 py-1 rounded-md text-sm font-medium ${!giftAll ? 'bg-pink-600 text-white' : 'text-gray-600'}`}>طلاب محددون</button>
              <button onClick={() => setGiftAll(true)}
                className={`px-3 py-1 rounded-md text-sm font-medium ${giftAll ? 'bg-pink-600 text-white' : 'text-gray-600'}`}>كل الطلاب</button>
            </div>

            {giftAll ? (
              <p className="text-xs text-pink-800 bg-pink-50 border border-pink-200 rounded-lg p-2">
                ستُرسل الهدية إلى جميع الطلاب المفعّلين ({students.length} طالب).
              </p>
            ) : (
              <div>
                <div className="flex items-center justify-between mb-1">
                  <Label className="text-xs">اختر الطلاب ({giftCount})</Label>
                  <button type="button" className="text-[11px] text-pink-600"
                    onClick={() => {
                      const all = {}; students.forEach((s) => { all[s.student_id] = true; }); setGiftSelected(all);
                    }}>تحديد الكل</button>
                </div>
                <div className="max-h-44 overflow-y-auto border border-[#E8DFCF] rounded-lg divide-y">
                  {students.length === 0 ? (
                    <p className="p-3 text-xs text-[#412402]/50 text-center">لا يوجد طلاب.</p>
                  ) : students.map((s) => (
                    <label key={s.student_id} className="flex items-center gap-2 p-2 text-sm cursor-pointer hover:bg-pink-50">
                      <input type="checkbox" checked={!!giftSelected[s.student_id]} onChange={() => toggleGiftStudent(s.student_id)} />
                      <span className="flex-1 truncate">{s.student_name}</span>
                      <span className="text-[11px] text-[#412402]/50">{s.grade}{s.section ? ` / ${s.section}` : ''}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            <div>
              <Label>قيمة الهدية ({UNIT})</Label>
              <Input type="number" min={1} step="1" value={giftAmount}
                onChange={(e) => setGiftAmount(e.target.value)} placeholder="مثال: 5" data-testid="gift-amount-input" />
            </div>
            <div>
              <Label>رسالة الهدية (اختياري)</Label>
              <Textarea rows={2} value={giftMsg} onChange={(e) => setGiftMsg(e.target.value)}
                placeholder="مثال: مكافأة على تفوقك 🎉" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setGiftOpen(false)}>إلغاء</Button>
            <Button onClick={submitGift} disabled={gifting} className="bg-pink-600 hover:bg-pink-700" data-testid="confirm-gift-btn">
              {gifting ? <Loader2 className="w-4 h-4 animate-spin ml-2" /> : <Gift className="w-4 h-4 ml-2" />}
              إرسال الهدية{giftAll ? ' للكل' : (giftCount ? ` (${giftCount})` : '')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TeacherWallet;
